<template>
    <div class="box">
        <div class="box-header with-border mb-3">
            <h3 class="box-title">Sauvegarder les subventions</h3>
            <div class="box-tools pull-right d-flex align-items-center">
                <select v-model="selectedYear" class="form-control mr-2" style="width: 100px;">
                    <option v-for="year in annees" :key="year" :value="year">{{ year }}</option>
                </select>
                <div class="custom-control custom-checkbox mr-2" v-if="configSubvention.locked">
                    <input type="checkbox" 
                           class="custom-control-input" 
                           id="forceUnlock" 
                           v-model="forceUnlock">
                    <label class="custom-control-label" for="forceUnlock">
                        Déverrouiller ces montants
                    </label>
                </div>
                <button class="btn btn-success" 
                        @click="validerSubventions" 
                        :disabled="configSubvention.locked && !forceUnlock">
                    <i class="fa fa-lock"></i> Sauvegarder et verrouiller les subventions
                </button>
            </div>
        </div>

        <div class="alert alert-warning" v-if="configSubvention.locked && forceUnlock">
            <i class="fa fa-exclamation-triangle"></i>
            <strong>Attention :</strong> Ces subventions sont verrouillées. Toute modification des montants doit être communiquée au service comptable.
        </div>

        <div class="box-config mb-4">
            <div class="row">
                <div class="col-md-3">
                    <div class="form-group mb-0">
                        <label>Montant de la subvention ADEPS</label>
                        <div class="input-group">
                            <input 
                                type="number" 
                                class="form-control" 
                                v-model.number="configSubvention.montantAdeps"
                                min="0"
                                step="1000"
                                :disabled="configSubvention.locked && !forceUnlock"
                            >
                            <div class="input-group-append">
                                <span class="input-group-text">€</span>
                            </div>
                        </div>
                        <small class="text-muted" v-if="subventionOption">
                            Dernière modification le {{ formatDateTime(subventionOption.updated_at) }}
                        </small>
                    </div>
                </div>
                <div class="col-md-3">
                    <div class="form-group mb-0">
                        <label>% dédié à la gestion de projet</label>
                        <div class="input-group">
                            <input 
                                type="number" 
                                class="form-control" 
                                v-model.number="configSubvention.pourcentageGestion"
                                min="0"
                                max="100"
                                step="1"
                                :disabled="configSubvention.locked && !forceUnlock"
                            >
                            <div class="input-group-append">
                                <span class="input-group-text">%</span>
                            </div>
                        </div>
                        <small class="text-muted" >
                            Subvention restante à distribuer : {{ formatMontant(configSubvention.montantAdeps - (configSubvention.montantAdeps * (configSubvention.pourcentageGestion / 100))) }}
                        </small>
                    </div>
                </div>
                <div class="col-md-3">
                    <div class="form-group mb-0">
                        <label>Montant minimum finançable</label>
                        <div class="input-group">
                            <input 
                                type="number" 
                                class="form-control" 
                                v-model.number="configSubvention.seuilMinimum"
                                min="0"
                                step="100"
                                :disabled="configSubvention.locked && !forceUnlock"
                            >
                            <div class="input-group-append">
                                <span class="input-group-text">€</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-md-3">
                    <div class="form-group mb-0">
                        <label>&nbsp;</label>
                        <div class="input-group">
                            <button 
                                class="btn btn-primary w-100" 
                                @click="recalculerSubventions"
                                :disabled="configSubvention.locked && !forceUnlock"
                            >
                                <i class="fa fa-refresh"></i> Recalculer les subventions
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="box-body">
            <div v-if="loading" class="loading-container">
                <i class="fa fa-spinner fa-spin fa-2x"></i>
                <span>Chargement des subventions...</span>
            </div>
            <div v-else class="table-responsive">
                <table class="table table-bordered table-striped">
                    <thead>
                        <tr>
                            <th @click="sortBy('club')" class="sortable">
                                Club
                                <i :class="['fa', sortKey === 'club' ? (sortOrders.club > 0 ? 'fa-sort-asc' : 'fa-sort-desc') : 'fa-sort']"></i>
                            </th>
                            <th colspan="5" class="text-center">Points obtenus</th>
                            <th colspan="3" class="text-center">Labels</th>
                            <th @click="sortBy('subventionARecevoir')" class="sortable">
                                Montant calculé
                                <i :class="['fa', sortKey === 'subventionARecevoir' ? (sortOrders.subventionARecevoir > 0 ? 'fa-sort-asc' : 'fa-sort-desc') : 'fa-sort']"></i>
                            </th>
                            <th>Montant final attribué</th>
                        </tr>
                        <tr>
                            <th></th>
                            <th @click="sortBy('commun')" class="text-center sortable">
                                Commun
                                <i :class="['fa', sortKey === 'commun' ? (sortOrders.commun > 0 ? 'fa-sort-asc' : 'fa-sort-desc') : 'fa-sort']"></i>
                            </th>
                            <th @click="sortBy('artMartial')" class="text-center sortable">
                                Art Martial
                                <i :class="['fa', sortKey === 'artMartial' ? (sortOrders.artMartial > 0 ? 'fa-sort-asc' : 'fa-sort-desc') : 'fa-sort']"></i>
                            </th>
                            <th @click="sortBy('kyorugi')" class="text-center sortable">
                                Kyorugi
                                <i :class="['fa', sortKey === 'kyorugi' ? (sortOrders.kyorugi > 0 ? 'fa-sort-asc' : 'fa-sort-desc') : 'fa-sort']"></i>
                            </th>
                            <th @click="sortBy('poomsae')" class="text-center sortable">
                                Poomsae
                                <i :class="['fa', sortKey === 'poomsae' ? (sortOrders.poomsae > 0 ? 'fa-sort-asc' : 'fa-sort-desc') : 'fa-sort']"></i>
                            </th>
                            <th @click="sortBy('totalPoints')" class="text-center bg-light sortable">
                                <strong>Total</strong>
                                <i :class="['fa', sortKey === 'totalPoints' ? (sortOrders.totalPoints > 0 ? 'fa-sort-asc' : 'fa-sort-desc') : 'fa-sort']"></i>
                            </th>
                            <th class="text-center">Art Martial</th>
                            <th class="text-center">Kyorugi</th>
                            <th class="text-center">Poomsae</th>
                            <th></th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="data in sortedClubs" :key="data.id">
                            <td><a :href="'club/' + data.id">{{ data.club.numero_club }} - {{ data.club.club.toUpperCase() }}</a></td>
                            <!-- Points -->
                            <td class="text-center" :class="getPointsClass(data, 'commun')">
                                <span v-if="isPointsValid(data, 'commun')">
                                    {{ getTotal(data, 'commun') }}
                                </span>
                                <span v-else class="text-danger">
                                    0
                                </span>
                            </td>
                            <td class="text-center" :class="getPointsClass(data, 'artMartial')">
                                <span v-if="isPointsValid(data, 'artMartial')"
                                      :title="getCalculDetail(data, 'artMartial')"
                                      class="points-value">
                                    {{ getTotal(data, 'artMartial') }}
                                </span>
                                <span v-else class="text-danger">
                                    0
                                </span>
                            </td>
                            <td class="text-center" :class="getPointsClass(data, 'kyorugi')">
                                <span v-if="isPointsValid(data, 'kyorugi')"
                                      :title="getCalculDetail(data, 'kyorugi')"
                                      class="points-value">
                                    {{ getTotal(data, 'kyorugi') }}
                                </span>
                                <span v-else class="text-danger">
                                    0
                                </span>
                            </td>
                            <td class="text-center" :class="getPointsClass(data, 'poomsae')">
                                <span v-if="isPointsValid(data, 'poomsae')"
                                      :title="getCalculDetail(data, 'poomsae')"
                                      class="points-value">
                                    {{ getTotal(data, 'poomsae') }}
                                </span>
                                <span v-else class="text-danger">
                                    0
                                </span>
                            </td>
                            <td class="text-center bg-light">
                                <strong>{{ getTotalPointsForClub(data).toFixed(1) }} </strong>
                            </td>
                            <!-- Labels -->
                            <td class="text-center">
                                <span v-if="getLabelNiveau(data.labels, 'Art Martial')" class="badge badge-success">
                                    {{ getLabelNiveau(data.labels, 'Art Martial') }} ⭐
                                </span>
                                <span v-else>-</span>
                            </td>
                            <td class="text-center">
                                <span v-if="getLabelNiveau(data.labels, 'Kyorugi')" class="badge badge-success">
                                    {{ getLabelNiveau(data.labels, 'Kyorugi') }} ⭐
                                </span>
                                <span v-else>-</span>
                            </td>
                            <td class="text-center">
                                <span v-if="getLabelNiveau(data.labels, 'Poomsae')" class="badge badge-success">
                                    {{ getLabelNiveau(data.labels, 'Poomsae') }} ⭐
                                </span>
                                <span v-else>-</span>
                            </td>
                            <td>{{ formatMontant(data.subventionARecevoir) }}</td>
                            <td>
                                <div class="input-group">
                                    <input
                                        type="number"
                                        class="form-control"
                                        v-model.number="tempMontants[data.id]"
                                        :placeholder="data.subventionARecevoir ? data.subventionARecevoir.toFixed(2) : '0.00'"
                                        min="0"
                                        step="100"
                                        :disabled="configSubvention.locked && !forceUnlock"
                                        @input="forceUpdate()"
                                    >
                                    <div class="input-group-append">
                                        <button 
                                            class="btn btn-outline-secondary" 
                                            type="button"
                                            title="Copier le montant calculé"
                                            @click="copierMontantCalcule(data, data.id)"
                                        >
                                            <i class="fa fa-copy"></i>
                                        </button>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                    <tfoot>
                        <tr>
                            <td><strong>Total</strong></td>
                            <!-- Points totaux -->
                            <td class="text-center"><strong>{{ totalPoints('commun') }}</strong></td>
                            <td class="text-center"><strong>{{ totalPoints('artMartial') }}</strong></td>
                            <td class="text-center"><strong>{{ totalPoints('kyorugi') }}</strong></td>
                            <td class="text-center"><strong>{{ totalPoints('poomsae') }}</strong></td>
                            <td class="text-center bg-light"><strong>{{ totalAllPoints.toFixed(1) }}</strong></td>
                            <!-- Espaces pour les colonnes de labels -->
                            <td></td>
                            <td></td>
                            <td></td>
                            <td><strong>{{ formatMontant(totalMontantCalcule) }}</strong></td>
                            <td><strong>{{ formatMontant(totalMontantFinal) }}</strong></td>
                        </tr>
                    </tfoot>
                </table>
                <div class="mt-3 text-right">
                    <button 
                        class="btn btn-success"
                        @click="exportCSV"
                        :disabled="loading"
                    >
                        <i class="fa fa-file-text-o"></i> Exporter en CSV
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
    name: 'Subventions',
    props: {
        annee: {
            type: Number,
            required: true
        }
    },
    data() {
        return {
            clubsData: {},
            loading: false,
            subventionsVerrouillees: false,
            configSubvention: {
                montantAdeps: 0,
                pourcentageGestion: 0,
                seuilMinimum: 0,
                locked: false
            },
            forceUnlock: false,
            subventionOption: null,
            annees: [],
            selectedYear: this.annee,
            tempMontants: {},
            updateTrigger: 0,
            sortKey: 'club',
            sortOrders: {
                club: 1,
                commun: 1,
                artMartial: 1,
                kyorugi: 1,
                poomsae: 1,
                totalPoints: 1,
                subventionARecevoir: 1
            }
        }
    },
    computed: {
        ...mapGetters({
            getRoute: 'getRoute'
        }),
        totalMontantCalcule() {
            return Object.values(this.clubsData).reduce((total, data) => total + (data.subventionARecevoir || 0), 0)
        },
        totalAllPoints() {
            return Object.values(this.clubsData).reduce((total, data) => {
                return total + this.getTotalPointsForClub(data);
            }, 0);
        },
        totalMontantFinal() {
            this.updateTrigger;
            return Object.values(this.tempMontants).reduce((total, montant) => {
                const value = parseFloat(montant) || 0;
                return total + value;
            }, 0);
        },
        sortedClubs() {
            const clubs = Object.entries(this.clubsData).map(([clubId, data]) => ({
                id: clubId,
                ...data,
                totalPoints: this.getTotalPointsForClub(data)
            }));

            return clubs.sort((a, b) => {
                let modifier = this.sortOrders[this.sortKey];
                let aValue, bValue;

                switch(this.sortKey) {
                    case 'club':
                        aValue = a.club.numero_club;
                        bValue = b.club.numero_club;
                        break;
                    case 'commun':
                        aValue = this.getTotal(a, 'commun');
                        bValue = this.getTotal(b, 'commun');
                        break;
                    case 'artMartial':
                        aValue = this.getTotal(a, 'artMartial');
                        bValue = this.getTotal(b, 'artMartial');
                        break;
                    case 'kyorugi':
                        aValue = this.getTotal(a, 'kyorugi');
                        bValue = this.getTotal(b, 'kyorugi');
                        break;
                    case 'poomsae':
                        aValue = this.getTotal(a, 'poomsae');
                        bValue = this.getTotal(b, 'poomsae');
                        break;
                    case 'totalPoints':
                        aValue = a.totalPoints;
                        bValue = b.totalPoints;
                        break;
                    case 'subventionARecevoir':
                        aValue = a.subventionARecevoir;
                        bValue = b.subventionARecevoir;
                        break;
                    default:
                        return 0;
                }

                if (aValue > bValue) return modifier;
                if (aValue < bValue) return -modifier;
                return 0;
            });
        }
    },
    created() {
        const currentYear = new Date().getFullYear();
        this.annees = [];
        for (let year = 2030; year >= currentYear; year--) {
            this.annees.push(year);
        }
    },
    mounted() {
        this.chargerSubventions()
    },
    watch: {
        selectedYear(newYear) {
            this.chargerSubventions();
        },
        tempMontants: {
            deep: true,
            handler() {
                this.forceUpdate();
            }
        },
        annee: {
            immediate: true,
            handler(newValue) {
                this.selectedYear = newValue;
            }
        }
    },
    methods: {
        forceUpdate() {
            this.updateTrigger += 1;
        },
        updateMontant(clubId, value) {
            const numeroValue = value === '' ? 0 : parseFloat(value);
            this.$set(this.tempMontants, clubId, numeroValue);
        },
    
        chargerSubventions() {
            this.loading = true;
            this.$http.post(this.getRoute('Labelisation.calculateSubvention'), {
                annee: this.selectedYear,
                updateSubventionOption: false
            })
                .then(response => {
                    this.clubsData = response.body.clubs;
                    // Initialiser les montants temporaires avec les valeurs formatées
                    this.tempMontants = {};
                    Object.entries(this.clubsData).forEach(([clubId, data]) => {
                        const montant = data.subventionARecevoir || 0;
                        this.tempMontants[clubId] = Number(montant.toFixed(2));
                    });
                    if (response.body.subventionOption) {
                        this.subventionOption = response.body.subventionOption;
                        this.configSubvention.montantAdeps = this.subventionOption.subventionAdeps;
                        this.configSubvention.pourcentageGestion = this.subventionOption.pourcentageDedie * 100;
                        this.configSubvention.seuilMinimum = this.subventionOption.seuilMinimumFinancier;
                        this.configSubvention.locked = this.subventionOption.locked;
                        if (this.configSubvention.locked) {
                            let montantsAttribuesAndLocked=response.body.montantsAttribuesAndLocked;
                            Object.entries(montantsAttribuesAndLocked).forEach(([clubId, montant]) => {
                                this.tempMontants[clubId] = Number(montant.toFixed(2));
                            });
                        }
                    }
                    this.subventionsVerrouillees = Object.values(response.body).some(data => data.montantFinal !== undefined);
                })
                .catch(error => {
                    console.error('Erreur lors du chargement des subventions:', error);
                    this.$noty.error('Erreur lors du chargement des subventions');
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        sauvegarderSubvention(clubId, data) {
            const montantFinal = data.montantFinal || data.montant
            this.$http.post(this.getRoute('Subventions.save'), {
                annee: this.selectedYear,
                clubId: parseInt(clubId),
                montant: montantFinal
            })
            .then(() => {
                this.$noty.success('Subvention sauvegardée avec succès');
                // Mettre à jour la donnée locale
                this.$set(this.clubsData[clubId], 'subventionARecevoir', montantFinal)
            })
            .catch(error => {
                console.error('Erreur lors de la sauvegarde de la subvention:', error);
                this.$noty.error('Erreur lors de la sauvegarde de la subvention');
            })
        },
        validerSubventions() {
            if (!confirm('Êtes-vous sûr de vouloir sauvegarder et verrouiller toutes les subventions ?')) {
                return;
            }

            if (this.configSubvention.locked && !this.forceUnlock) {
                this.$noty.error('Les subventions ont été sauvegardées et verrouillées. Vous devez déverrouiller les montants pour pouvoir les modifier.');
                return;
            }

            this.loading = true;
            this.$http.post(this.getRoute('Labelisation.saveAndLockSubvention'), { 
                annee: this.selectedYear,
                montantsAttribues: this.tempMontants,
                subventionAdeps: this.configSubvention.montantAdeps,
                pourcentageGestion: this.configSubvention.pourcentageGestion / 100,
                seuilMinimumFinancier: this.configSubvention.seuilMinimum
            })
                .then(() => {
                    this.$noty.success('Les subventions ont été sauvegardées et verrouillées avec succès');
                    this.subventionsVerrouillees = true
                    this.forceUnlock=false;
                    this.configSubvention.locked=true;
                })
                .catch(error => {
                    console.error('Erreur lors de la sauvegarde et du verrouillage des subventions:', error);
                    this.$noty.error('Erreur lors de la sauvegarde et du verrouillage des subventions');
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        formatMontant(montant) {
            return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(montant)
        },
        copierMontantCalcule(data, clubId) {
            if (data.subventionARecevoir) {
                this.$set(this.tempMontants, clubId, Number(data.subventionARecevoir.toFixed(2)));
                this.forceUpdate();
                // this.$noty.success('Montant copié');
            }
        },
        recalculerSubventions() {
            
            this.loading = true;
            const params = {
                annee: this.selectedYear,
                subventionAdeps: this.configSubvention.montantAdeps,
                pourcentageGestion: this.configSubvention.pourcentageGestion / 100,
                seuilMinimumFinancier: this.configSubvention.seuilMinimum,
                updateSubventionOption: true
            };
            this.$http.post(this.getRoute('Labelisation.calculateSubvention'), params)
                .then(response => {
                    this.clubsData = response.body.clubs;
                    // Réinitialiser les montants temporaires avec les nouvelles valeurs calculées
                    this.tempMontants = {};
                    Object.entries(this.clubsData).forEach(([clubId, data]) => {
                        const montant = data.montantFinal || data.montant || 0;
                        this.tempMontants[clubId] = Number(montant.toFixed(2));
                    });
                    if (response.body.subventionOption) {
                        this.subventionOption = response.body.subventionOption;
                    }
                    this.$noty.success('Subventions recalculées avec succès');
                })
                .catch(error => {
                    console.error('Erreur lors du recalcul des subventions:', error);
                    this.$noty.error('Erreur lors du recalcul des subventions');
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        getLabelNiveau(labels, nomLabel) {
            if (!labels || !Array.isArray(labels)) return 0;
            const label = labels.find(l => l.label && l.label.nom === nomLabel);
            return label ? label.niveau : 0;
        },
        getPoints(data, type) {
            if (!data || !data.details || !data.details.details || !data.details.details[type]) {
                return 0;
            }
            return data.details.details[type].points || 0;
        },
        totalPoints(type) {
            return Object.values(this.clubsData).reduce((total, data) => {
                return total + this.getPoints(data, type);
            }, 0);
        },
        getPointsClass(data, type) {
            if (!data || !data.details || !data.details.details || !data.details.details[type]) {
                return '';
            }
            return data.details.details[type].total === 0 ? 'text-danger' : 'text-success';
        },
        isPointsValid(data, type) {
            if (!data || !data.details || !data.details.details || !data.details.details[type]) {
                return true;
            }
            return data.details.details[type].total !== 0;
        },
        getTotalPointsForClub(data) {
            if (!data || !data.details || !data.details.details) {
                return 0;
            }
            const total = Object.values(data.details.details).reduce((total, category) => {
                return total + (category.total || 0);
            }, 0);
            return Number(total);
        },
        getTotal(data, type) {
            if (!data || !data.details || !data.details.details || !data.details.details[type]) {
                return 0;
            }
            return Number(data.details.details[type].total || 0).toFixed(1);
        },
        getCoefficient(data, type) {
            if (!data || !data.details || !data.details.details || !data.details.details[type]) {
                return 0;
            }
            return Number(data.details.details[type].coefficient || 0).toFixed(1);
        },
        formatPoints(data, type) {
            const points = this.getPoints(data, type);
            const total = this.getTotal(data, type);
            const coefficient = this.getCoefficient(data, type);
            
            if (points === total) {
                return total;
            }
            return `<span class="text-gray text-sm">${points} * ${coefficient} = </span> ${total} `;
        },
        getCalculDetail(data, type) {
            const points = this.getPoints(data, type);
            const coefficient = this.getCoefficient(data, type);
            const total = this.getTotal(data, type);
            
            if (points === Number(total)) {
                return `${points} points`;
            }
            return `Calcul: ${points} points × ${coefficient} = ${total}`;
        },
        formatDateTime(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        },
        exportCSV() {
            // Préparer les en-têtes
            const headers = [
                'Numéro Club',
                'Club',
                'Points Commun',
                'Points Art Martial',
                'Points Kyorugi',
                'Points Poomsae',
                'Total Points',
                'Label Art Martial',
                'Label Kyorugi',
                'Label Poomsae',
                'Montant calculé',
                'Montant final attribué'
            ];

            // Préparer les données
            const rows = Object.entries(this.clubsData).map(([clubId, data]) => [
                data.club.numero_club,
                data.club.club,
                this.getTotal(data, 'commun'),
                this.getTotal(data, 'artMartial'),
                this.getTotal(data, 'kyorugi'),
                this.getTotal(data, 'poomsae'),
                this.getTotalPointsForClub(data).toFixed(1),
                this.getLabelNiveau(data.labels, 'Art Martial'),
                this.getLabelNiveau(data.labels, 'Kyorugi'),
                this.getLabelNiveau(data.labels, 'Poomsae'),
                data.subventionARecevoir.toFixed(2),
                this.tempMontants[clubId].toFixed(2)
            ]);

            // Convertir en CSV
            const csvContent = [
                headers.join(';'),
                ...rows.map(row => row.join(';'))
            ].join('\n');

            // Créer et télécharger le fichier
            const blob = new Blob(['\ufeff' + csvContent], { // \ufeff est le BOM pour Excel
                type: 'text/csv;charset=utf-8'
            });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `subventions_${this.selectedYear}.csv`;
            link.click();

            this.$noty.success('Export CSV généré avec succès');
        },
        sortBy(key) {
            this.sortOrders[key] = this.sortKey === key ? -this.sortOrders[key] : 1;
            this.sortKey = key;
        }
    }
}
</script>

<style scoped>
.loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px;
    gap: 15px;
    color: #666;
}

.loading-container i {
    color: #007bff;
}

.box-tools {
    display: flex;
    align-items: center;
    gap: 10px;
}

.box-tools select {
    flex-shrink: 0;
}

.box-tools button {
    white-space: nowrap;
}

.form-control {
    width: 150px;
}

table tfoot {
    background-color: #f9f9f9;
}

table tfoot td {
    border-top: 2px solid #dee2e6;
}

.input-group {
    display: flex;
    align-items: stretch;
    width: 100%;
}

.input-group .form-control {
    height: 34px;
}

.input-group-append .btn {
    height: 34px;
    padding: 6px 12px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.input-group-append .btn i {
    font-size: 14px;
}

.input-group-append {
    display: flex;
}

.input-group-text {
    height: 34px;
    padding: 6px 12px;
    display: flex;
    align-items: center;
}

.btn-outline-secondary {
    color: #6c757d;
    border-color: #6c757d;
    background-color: white;
}

.btn-outline-secondary:hover {
    color: white;
    background-color: #6c757d;
}

.mb-3 {
    margin-bottom: 1rem;
}

.mb-4 {
    margin-bottom: 1.5rem;
}

.box-config {
    padding: 1rem;
    background-color: #f8f9fa;
    border-radius: 4px;
}

.box-config .form-group {
    margin-bottom: 0;
}

.box-config .input-group {
    width: 100%;
}

.box-config .form-control {
    width: 100%;
}

.form-group {
    margin-bottom: 1rem;
}

.form-group label {
    display: block;
    margin-bottom: 0.5rem;
    min-height: 21px;
}

.d-flex {
    display: flex;
}

.align-items-end {
    align-items: flex-end;
}

.text-center {
    text-align: center;
}

.badge {
    padding: 0.4em 0.6em;
    font-size: 85%;
    font-weight: 600;
    border-radius: 0.25rem;
}

.badge-success {
    color: #fff;
    background-color: #28a745;
}

thead th {
    vertical-align: middle !important;
}

.text-danger {
    color: #dc3545;
}

.text-success {
    color: #28a745;
}

.bg-light {
    background-color: #f8f9fa;
}

.points-value {
    cursor: help;
    border-bottom: 1px dotted #666;
}

.w-100 {
    width: 100%;
}

.mb-0 {
    margin-bottom: 0 !important;
}

.mt-3 {
    margin-top: 1rem;
}

.text-right {
    text-align: right;
}

.sortable {
    cursor: pointer;
    user-select: none;
}

.sortable i {
    margin-left: 5px;
}

th.sortable:hover {
    background-color: #f5f5f5;
}
</style> 


// WEBPACK FOOTER //
// src/components/Labelisation/Subventions.vue